import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  startOfDay,
  startOfMonth,
} from "date-fns"
import { formatDate, weekStart } from "lopermut"
import { BiCategory, BiCategoryField, DateRange, TimeDimension, TimeDimensionEnum } from "schema"

export type BiBucket = {
  key: string
  label: string
  days: string[]
}

export type BiAggregation = "sum" | "mean" | "last"

export const LAST_VALUE_FIELDS = [
  {
    category: BiCategory.Organization,
    field: BiCategoryField.Organization.SubscriptionPrice,
  },
  {
    category: BiCategory.Organization,
    field: BiCategoryField.Organization.SubscriptionCurrentPrice,
  },
]

export const getBiBuckets = (range: DateRange, dimension: TimeDimension) => {
  const buckets = new Map<string, BiBucket>()
  const today = startOfDay(new Date())

  for (const day of eachDayOfInterval({ start: range.from, end: range.to })) {
    if (day >= today) continue

    const start = getBucketStart(day, range.from, dimension)
    const key = format(start, "dd/MM/yyyy")
    const bucket: BiBucket = buckets.get(key) ?? {
      key,
      label: formatDate(start, dimension === TimeDimensionEnum.Month ? "MMM yyyy" : "MMM d"),
      days: [],
    }

    bucket.days.push(format(day, "dd/MM/yyyy"))
    buckets.set(key, bucket)
  }

  return [...buckets.values()]
}

export const aggregateBucket = (values: number[], aggregation: BiAggregation) => {
  if (values.length === 0) return 0

  switch (aggregation) {
    case "last":
      return values[values.length - 1] ?? 0
    case "mean":
      return values.reduce((sum, value) => sum + value, 0) / values.length
    case "sum":
      return values.reduce((sum, value) => sum + value, 0)
  }
}

export const getBiAggregation = ({
  type,
  category,
  field,
}: {
  type: "Money" | "Count" | "Rate"
  category: string
  field: string
}) => {
  if (type === "Rate") return "mean"

  if (LAST_VALUE_FIELDS.some((metric) => metric.category === category && metric.field === field)) {
    return "last"
  }

  return "sum"
}

const getBucketStart = (day: Date, from: Date, dimension: TimeDimension) => {
  switch (dimension) {
    case TimeDimensionEnum.ThreeDays:
      return addDays(startOfDay(from), Math.floor(differenceInCalendarDays(day, from) / 3) * 3)
    case TimeDimensionEnum.Week:
      return weekStart(day)
    case TimeDimensionEnum.Month:
      return startOfMonth(day)
    case TimeDimensionEnum.Day:
    case TimeDimensionEnum.TwelveHours:
    case TimeDimensionEnum.ThreeHours:
    case TimeDimensionEnum.Hour:
    case TimeDimensionEnum.ThirtyMinutes:
    case TimeDimensionEnum.TenMinutes:
      return day
  }
}
